import { resultHash } from './hash.ts';

/** Which result a person is looking at: the same five values on the web page and in the chat. */
export interface Stamp {
  readonly plan_id: string;
  readonly plan_name: string;
  readonly plan_version: number;
  readonly as_of: string;
  readonly result_hash: string;
  /** The latest waypoint the result is anchored to (waypoints are not plan versions). */
  readonly waypoint_id: string | null;
}

export interface StampMeta {
  readonly planId: string;
  readonly planName: string;
  readonly version: number;
  readonly asOf: string;
  readonly waypointId: string | null;
}

export async function stampFor(result: Record<string, unknown>, meta: StampMeta): Promise<Stamp> {
  return {
    plan_id: meta.planId,
    plan_name: meta.planName,
    plan_version: meta.version,
    as_of: meta.asOf,
    result_hash: await resultHash(result),
    waypoint_id: meta.waypointId,
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** "2026-10-10" → "10 Oct 2026"; independent of locale and time zone. */
export function readableDate(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** "2026-10-01" → "Oct 2026". */
export function readableMonth(day: string): string {
  const [y, m] = day.split('-').map(Number) as [number, number];
  return `${MONTHS[m - 1]} ${y}`;
}

/** "Plan v12 · as of 10 Oct 2026 · result 7f3ac91e": printed identically by the web page and the tool text. */
export function stampLine(stamp: Pick<Stamp, 'plan_version' | 'as_of'> & { readonly result_hash?: string | null }): string {
  const parts = [`Plan v${stamp.plan_version}`, `as of ${readableDate(stamp.as_of)}`];
  if (stamp.result_hash) parts.push(`result ${stamp.result_hash}`);
  return parts.join(' · ');
}
